import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { FaBoxes, FaBoxOpen } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import CurrencyInput from '~/components/Inputs/Currency';
import DefaultInput from '~/components/Inputs/Default';
import ExitButton from '~/components/Buttons/Exit';
import SaveButton from '~/components/Buttons/Save';
import Toggle from '~/components/Toggle';

import { getCurrencyFormatValue } from '~/utils';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Wrapper } from './styles';

interface IFormSample {
  name: string;
  erp_code: string;
  active: boolean;
  price: number;
}

interface ILocation {
  id: string;
  name: string;
  erp_code: string;
  active: boolean;
  price: number;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
});

const New: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, control, errors } = useForm<IFormSample>({
    context: {
      erpCodeEnabled: state === undefined,
    },
    resolver: yupResolver(schema),
    defaultValues: {
      active: state?.active,
    },
  });

  const submitForm = useCallback(
    async (data: IFormSample) => {
      try {
        setIsSaving(true);

        if (state === undefined) {
          await api.post('/items/samples', {
            ...data,
            price: getCurrencyFormatValue(data.price),
          });

          addToast({
            type: 'success',
            title: 'Cadastro realizado!',
            description: 'Amostra cadastrada com sucesso.',
          });
        } else {
          await api.put(`/items/samples`, {
            id: state.id,
            ...data,
            price: getCurrencyFormatValue(data.price),
          });

          addToast({
            type: 'success',
            title: 'Cadastro atualizado!',
            description: 'Amostra atualizada com sucesso.',
          });
        }

        history.push('/items/samples');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, state],
  );

  const onExit = () => {
    history.push('/items/samples');
  };

  return (
    <Container>
      <BreadCrumb
        main="Itens"
        path="/items/main"
        firstChildPath="/items/samples"
        firstChild="Amostras"
        secondChild={state === undefined ? 'Nova Amostra' : 'Edição de Amostra'}
        mainIcon={FaBoxes}
        firstChildIcon={FaBoxOpen}
        secondChildIcon={FaBoxOpen}
        mainColored
        firstChildColored
      />

      <form onSubmit={handleSubmit(submitForm)}>
        <div
          style={{ maxWidth: 700, display: 'flex', flexWrap: 'wrap', gap: 10 }}
        >
          <Wrapper style={{ width: 180 }}>
            <DefaultInput
              name="erp_code"
              labelFor="erp_code"
              labelText="Código ERP"
              type="text"
              defaultValue={state?.erp_code}
              error={errors?.erp_code?.message}
              ref={register}
              maxLength={10}
              readOnly={
                state?.erp_code !== null &&
                state?.erp_code !== undefined &&
                state?.erp_code !== ''
              }
            />
          </Wrapper>

          <Wrapper style={{ width: 350 }}>
            <DefaultInput
              name="name"
              labelFor="name"
              labelText="Nome"
              type="text"
              defaultValue={state?.name}
              error={errors?.name?.message}
              ref={register}
              maxLength={60}
            />
          </Wrapper>

          <Wrapper style={{ width: 100 }}>
            <Controller
              render={props => (
                <Toggle
                  onChange={e => props.onChange(e)}
                  checked={props.value}
                />
              )}
              name="active"
              defaultValue
              control={control}
            />
          </Wrapper>

          <Wrapper style={{ width: 180 }}>
            <CurrencyInput
              name="price"
              labelFor="price"
              labelText="Valor"
              defaultValue={state?.price || 0}
              error={errors?.price?.message}
              ref={register}
            />
          </Wrapper>
        </div>

        <div
          style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end' }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title="Confirma gravação da amostra?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />
    </Container>
  );
};

export default New;
